/**
 * Global styles for UI/UX enhancements
 * Implements VPT-103: Focus states, accessibility, and visual polish
 */

export const globalStyles = {
  // Enhanced focus states for better keyboard navigation
  focusVisible: {
    '&:focus-visible': {
      outline: '2px solid',
      outlineColor: 'primary.main',
      outlineOffset: 2,
      transition: 'outline-offset 0.2s ease',
    },
  },

  // Consistent button focus states
  buttonFocus: {
    '&:focus-visible': {
      outline: '3px solid',
      outlineColor: 'primary.light',
      outlineOffset: 2,
      boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.1)',
    },
  },

  // Input field focus enhancement
  inputFocus: {
    '& .MuiOutlinedInput-root': {
      '&:focus-within': {
        boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
      },
      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderWidth: 2,
        },
      },
    },
  },

  // Card hover and focus states
  interactiveCard: {
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: 4,
    },
    '&:focus-visible': {
      outline: '2px solid',
      outlineColor: 'primary.main',
      outlineOffset: 2,
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },

  // Loading skeleton animation
  skeletonPulse: {
    '@keyframes pulse': {
      '0%': {
        opacity: 1,
      },
      '50%': {
        opacity: 0.4,
      },
      '100%': {
        opacity: 1,
      },
    },
    animation: 'pulse 1.5s ease-in-out infinite',
  },

  // Smooth transitions
  smoothTransition: {
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  // Success animation
  successAnimation: {
    '@keyframes checkmark': {
      '0%': {
        transform: 'scale(0)',
        opacity: 0,
      },
      '50%': {
        transform: 'scale(1.2)',
      },
      '100%': {
        transform: 'scale(1)',
        opacity: 1,
      },
    },
    animation: 'checkmark 0.5s ease-out',
  },

  // Error shake animation
  errorShake: {
    '@keyframes shake': {
      '0%, 100%': {
        transform: 'translateX(0)',
      },
      '10%, 30%, 50%, 70%, 90%': {
        transform: 'translateX(-4px)',
      },
      '20%, 40%, 60%, 80%': {
        transform: 'translateX(4px)',
      },
    },
    animation: 'shake 0.5s ease-in-out',
  },

  // Spinner rotation
  spinner: {
    '@keyframes spin': {
      '0%': {
        transform: 'rotate(0deg)',
      },
      '100%': {
        transform: 'rotate(360deg)',
      },
    },
    animation: 'spin 1s linear infinite',
  },

  // Skip to content link (accessibility)
  skipLink: {
    position: 'absolute',
    top: -40,
    left: 0,
    background: 'background.paper',
    color: 'primary.main',
    padding: '8px',
    zIndex: 100,
    textDecoration: 'none',
    '&:focus': {
      top: 0,
    },
  },

  // High contrast mode support
  highContrast: {
    '@media (prefers-contrast: high)': {
      borderWidth: 2,
      fontWeight: 600,
    },
  },

  // Reduced motion support
  reducedMotion: {
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      transition: 'none',
    },
  },

  // Active navigation indicator
  activeNav: {
    position: 'relative',
    fontWeight: 600,
    color: 'primary.main',
    backgroundColor: 'action.selected',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      width: 4,
      height: '70%',
      backgroundColor: 'primary.main',
      borderRadius: '0 2px 2px 0',
    },
  },

  // Tooltip enhancement
  enhancedTooltip: {
    fontSize: '0.875rem',
    padding: '8px 12px',
    maxWidth: 300,
    boxShadow: 3,
  },

  // Search field focus
  searchFocus: {
    '&:focus-within': {
      width: '100%',
      maxWidth: 600,
      transition: 'all 0.3s ease',
      '& .MuiInputBase-root': {
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      },
    },
  },
};

// Apply global styles to MUI theme
export const applyGlobalStyles = (theme) => ({
  ...theme,
  components: {
    ...theme.components,
    MuiBreadcrumbs: {
      ...theme.components?.MuiBreadcrumbs,
      defaultProps: {
        ...theme.components?.MuiBreadcrumbs?.defaultProps,
        separator: '›',
      },
      styleOverrides: {
        ...theme.components?.MuiBreadcrumbs?.styleOverrides,
        root: {
          ...theme.typography.caption,
          color: theme.palette.text.secondary,
          '& .MuiBreadcrumbs-separator': {
            marginLeft: theme.spacing(0.75),
            marginRight: theme.spacing(0.75),
            opacity: 0.6,
          },
          '& .MuiBreadcrumbs-li a': {
            color: theme.palette.text.secondary,
            textDecoration: 'none',
          },
          '& .MuiBreadcrumbs-li a:hover': {
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiButton: {
      ...theme.components?.MuiButton,
      styleOverrides: {
        ...theme.components?.MuiButton?.styleOverrides,
        root: {
          ...globalStyles.smoothTransition,
          ...globalStyles.buttonFocus,
        },
      },
    },
    MuiTextField: {
      ...theme.components?.MuiTextField,
      styleOverrides: {
        ...theme.components?.MuiTextField?.styleOverrides,
        root: globalStyles.inputFocus,
      },
    },
    MuiCard: {
      ...theme.components?.MuiCard,
      styleOverrides: {
        ...theme.components?.MuiCard?.styleOverrides,
        root: {
          ...globalStyles.smoothTransition,
        },
      },
    },
    MuiIconButton: {
      ...theme.components?.MuiIconButton,
      styleOverrides: {
        ...theme.components?.MuiIconButton?.styleOverrides,
        root: {
          ...globalStyles.focusVisible,
          ...globalStyles.smoothTransition,
        },
      },
    },
    MuiLink: {
      ...theme.components?.MuiLink,
      styleOverrides: {
        ...theme.components?.MuiLink?.styleOverrides,
        root: globalStyles.focusVisible,
      },
    },
    MuiListItemButton: {
      ...theme.components?.MuiListItemButton,
      styleOverrides: {
        ...theme.components?.MuiListItemButton?.styleOverrides,
        root: {
          ...globalStyles.focusVisible,
          ...globalStyles.smoothTransition,
        },
      },
    },
    MuiTab: {
      ...theme.components?.MuiTab,
      styleOverrides: {
        ...theme.components?.MuiTab?.styleOverrides,
        root: {
          ...globalStyles.focusVisible,
          ...globalStyles.smoothTransition,
        },
      },
    },
  },
});

// Keyboard shortcuts handler
export const setupKeyboardShortcuts = () => {
  const handleKeyDown = (event) => {
    // Slash key focuses search
    if (event.key === '/' && !event.ctrlKey && !event.metaKey) {
      const searchInput = document.querySelector('[data-search-input]');
      if (searchInput && document.activeElement !== searchInput) {
        event.preventDefault();
        searchInput.focus();
      }
    }
    
    // Escape key blurs current element
    if (event.key === 'Escape') {
      const activeElement = document.activeElement;
      if (activeElement && activeElement.tagName !== 'BODY') {
        activeElement.blur();
      }
    }
    
    // Ctrl/Cmd + K opens command palette (if implemented)
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      event.preventDefault();
      const commandPaletteEvent = new CustomEvent('open-command-palette');
      window.dispatchEvent(commandPaletteEvent);
    }
  };

  document.addEventListener('keydown', handleKeyDown);
  
  // Cleanup function
  return () => {
    document.removeEventListener('keydown', handleKeyDown);
  };
};

export default globalStyles;
